import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Menu, X, ArrowRight, MessageSquareText, Repeat, BookOpenText,
  LineChart, ShieldCheck, Brain, Quote, Star,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Logo } from "@/components/Logo";
import { AppStoreBadges } from "@/components/AppStoreBadges";
import { PrimaryButton } from "@/components/common";
import { TryScenario } from "@/pages/TryScenario";
import { MomentsDemo } from "@/pages/MomentsDemo";

const NAV = [
  { label: "How it works", href: "#how" },
  { label: "Moments", href: "#moments" },
  { label: "Role-Play", href: "#try" },
  { label: "Stories", href: "#stories" },
  { label: "FAQ", href: "#faq" },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Landing() {
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    const id = window.location.hash?.replace("#", "");
    if (id) {
      const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <div className="min-h-screen bg-cream text-ink font-sans overflow-x-clip">
      <Header menu={menu} setMenu={setMenu} />
      <Hero />
      <Marquee />
      <HowItWorks />
      <MomentsDemo />
      <TryScenario />
      <Features />
      <Reflection />
      <Testimonials />
      <Faq />
      <DownloadCta />
      <Footer />
    </div>
  );
}

function Header({ menu, setMenu }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      className={`sticky top-0 z-50 backdrop-blur-md transition-all duration-300 ${
        scrolled ? "bg-cream/85 border-b border-cardborder shadow-[0_4px_20px_-8px_rgba(15,23,42,0.12)]" : "bg-cream/60 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="header-logo"><Logo /></Link>
        <nav className="hidden md:flex items-center gap-2">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} className="text-sm font-semibold text-ink rounded-full px-3.5 py-2 transition-colors hover:bg-sage-light hover:text-sage-hover">
              {n.label}
            </a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <PrimaryButton data-testid="header-try-it-btn" className="py-2.5 text-sm" onClick={() => scrollTo("moments")}>
            Try the demo
          </PrimaryButton>
        </div>
        <button className="md:hidden p-2 text-ink" onClick={() => setMenu(!menu)} data-testid="header-mobile-toggle" aria-label="Menu">
          {menu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {menu && (
        <div className="md:hidden border-t border-cardborder bg-cream px-4 py-4 flex flex-col gap-2">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} onClick={() => setMenu(false)} className="py-2 text-ink-muted font-medium">
              {n.label}
            </a>
          ))}
          <div className="flex gap-3 pt-2">
            <PrimaryButton className="flex-1 py-2.5 text-sm" onClick={() => { setMenu(false); scrollTo("moments"); }}>
              Try the demo
            </PrimaryButton>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section className="relative">
      <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-sage/10 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -right-20 h-80 w-80 rounded-full bg-amberx/10 blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <motion.span
            initial="hidden" animate="show" variants={fade}
            className="inline-flex items-center gap-2 rounded-full border border-sage/40 bg-sage-light px-4 py-1.5 text-xs font-mono font-medium uppercase tracking-[0.15em] text-sage-hover"
          >
            Practice for the moments that matter
          </motion.span>
          <motion.h1
            custom={1} initial="hidden" animate="show" variants={fade}
            className="mt-6 font-serif font-bold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-ink"
          >
            Turn tough moments into <span className="text-sage">personal breakthroughs.</span>
          </motion.h1>
          <motion.p
            custom={2} initial="hidden" animate="show" variants={fade}
            className="mt-6 text-lg text-ink-muted leading-relaxed max-w-xl"
          >
            Next Time helps you learn from difficult conversations, rehearse them with an AI partner, and walk in prepared, so you handle it better next time.
          </motion.p>
          <motion.div custom={3} initial="hidden" animate="show" variants={fade} className="mt-8 flex flex-wrap items-center gap-4">
            <PrimaryButton data-testid="hero-try-demo-btn" onClick={() => scrollTo("moments")} className="text-base">
              Try the free demo
            </PrimaryButton>
            <span className="text-sm text-ink-muted">No signup, no email</span>
          </motion.div>
          <motion.div custom={4} initial="hidden" animate="show" variants={fade} className="mt-8">
            <AppStoreBadges />
          </motion.div>
          <motion.div custom={5} initial="hidden" animate="show" variants={fade} className="mt-10 flex items-center gap-3">
            <div className="flex -space-x-2">
              {["#6B8E7B", "#D99B58", "#5A7E9A", "#A75D5D"].map((c, i) => (
                <span key={i} className="h-8 w-8 rounded-full border-2 border-cream" style={{ background: c }} />
              ))}
            </div>
            <div className="flex items-center gap-1 text-amberx">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              <span className="ml-1.5 text-sm text-ink-muted">Loved by reflective people</span>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <AppPreview />
        </motion.div>
      </div>
    </section>
  );
}

function AppPreview() {
  return (
    <div className="relative mx-auto max-w-sm">
      <div className="absolute -inset-4 rounded-[2.5rem] bg-ink/5 blur-2xl" />
      <div className="relative nt-float rounded-[2.25rem] border-8 border-ink bg-ink p-2 shadow-[0_30px_80px_-20px_rgba(15,23,42,0.45)]">
        <div className="rounded-[1.6rem] bg-cream overflow-hidden">
          <div className="px-5 pt-5 pb-3 flex items-center justify-between">
            <Logo />
            <span className="text-xs font-mono text-ink-muted">9:41</span>
          </div>
          <div className="px-5">
            <p className="font-serif text-xl text-ink">Good evening, Maya</p>
            <p className="text-sm text-ink-muted mt-1">Let's work on what happens next time.</p>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div className="bg-cardbg border border-cardborder rounded-2xl rounded-tl-sm p-3.5 text-sm text-ink shadow-sm">
              I hear you're frustrated, but I still think we should ship on Friday. What's your concern?
            </div>
            <div className="ml-auto max-w-[85%] bg-ink text-cream rounded-2xl rounded-tr-sm p-3.5 text-sm shadow-sm">
              I understand the deadline. I'm worried we're trading quality for speed, can we talk trade-offs?
            </div>
            <div className="bg-sage-light border border-sage/40 rounded-xl p-3 text-xs text-sage-hover flex gap-2 items-start">
              <span>Nice, you named the tension without escalating. Try proposing one concrete option next.</span>
            </div>
          </div>
          <div className="px-5 pb-5 flex gap-2">
            <div className="flex-1 rounded-full bg-cardbg border border-cardborder px-4 py-2.5 text-sm text-ink-muted">Type your reply…</div>
            <div className="h-10 w-10 rounded-full bg-sage grid place-items-center text-white"><ArrowRight className="h-4 w-4" /></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Marquee() {
  const stats = [
    { k: "Capture", v: "Log a moment in under a minute" },
    { k: "Analyze", v: "Facts vs. interpretation, always" },
    { k: "Practice", v: "Live AI role-play, any scenario" },
    { k: "Track", v: "See patterns shift over weeks" },
  ];
  return (
    <section className="border-y border-cardborder bg-cream-alt">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((s) => (
          <div key={s.k}>
            <p className="font-serif text-lg text-sage-hover">{s.k}</p>
            <p className="text-sm text-ink-muted mt-1">{s.v}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const STEPS = [
  { icon: MessageSquareText, title: "Capture a moment", body: "Something didn't go how you wanted? Jot down what happened while it's fresh, by typing or speaking." },
  { icon: Brain, title: "Get AI analysis", body: "See the likely trigger, what was in your control, and one stronger response, with facts kept separate from guesses." },
  { icon: Repeat, title: "Practice the role-play", body: "Rehearse the conversation live with an AI partner that pushes back, then get honest coaching feedback." },
  { icon: BookOpenText, title: "Build your playbook", body: "Turn what works into a personal strategy you can reach for the next time it happens." },
];

function HowItWorks() {
  const [active, setActive] = useState(0);
  return (
    <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
      <div className="max-w-2xl">
        <p className="text-xs font-mono uppercase tracking-[0.15em] text-sage-hover">How it works</p>
        <h2 className="mt-3 font-serif font-semibold tracking-tight text-3xl sm:text-4xl text-ink">A calm loop for handling hard things</h2>
        <p className="mt-4 text-lg text-ink-muted leading-relaxed">Four steps, repeated whenever life gets complicated. No shame, no diagnosis, just practical progress.</p>
      </div>
      <div className="mt-12 grid lg:grid-cols-2 gap-10 items-center">
        <div className="space-y-3">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const on = active === i;
            return (
              <button
                key={s.title}
                data-testid={`how-step-${i}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => setActive(i)}
                className={`w-full text-left flex gap-4 rounded-2xl p-5 border transition-all duration-300 ${
                  on ? "bg-cardbg border-sage shadow-md" : "bg-transparent border-cardborder hover:border-sage/50"
                }`}
              >
                <span className={`h-11 w-11 shrink-0 grid place-items-center rounded-xl transition-colors ${on ? "bg-sage text-white" : "bg-sage-light text-sage-hover"}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-serif text-lg text-ink flex items-center gap-2">
                    <span className="font-mono text-xs text-sage-hover">0{i + 1}</span> {s.title}
                  </p>
                  <p className={`text-sm text-ink-muted leading-relaxed mt-1 transition-all ${on ? "opacity-100" : "opacity-70"}`}>{s.body}</p>
                </div>
              </button>
            );
          })}
        </div>
        <div className="relative">
          <div className="rounded-3xl bg-ink text-cream p-8 lg:p-10 shadow-xl relative overflow-hidden nt-grain min-h-[360px] flex flex-col justify-between">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-sage-muted">Step 0{active + 1}</span>
              <h3 className="mt-4 font-serif text-2xl lg:text-3xl">{STEPS[active].title}</h3>
              <p className="mt-4 text-cream/70 leading-relaxed">{STEPS[active].body}</p>
            </div>
            <div className="mt-8 flex gap-1.5">
              {STEPS.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === active ? "w-8 bg-sage" : "w-1.5 bg-cream/25"}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  { icon: MessageSquareText, title: "AI conversation partner", body: "Rehearse a raise, a boundary, a breakup. The AI stays in character and pushes back at the difficulty you choose.", span: "lg:col-span-2", dark: true },
  { icon: LineChart, title: "Behavioral pattern spotter", body: "Recurring themes surface gently over time, from 'possible' to 'confirmed', so you can name what keeps happening." },
  { icon: BookOpenText, title: "Personal playbooks", body: "Your best responses, saved as go-to strategies for next time." },
  { icon: ShieldCheck, title: "Private & non-judgmental", body: "It's a practice tool, never a therapist. Your reflections stay yours, and you control your data.", span: "lg:col-span-2" },
];

function Features() {
  return (
    <section id="features" className="bg-cream-alt border-y border-cardborder">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="max-w-2xl">
          <p className="text-xs font-mono uppercase tracking-[0.15em] text-sage-hover">What's inside</p>
          <h2 className="mt-3 font-serif font-semibold tracking-tight text-3xl sm:text-4xl text-ink">Built to grow with every hard moment</h2>
        </div>
        <div className="mt-12 grid lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i} variants={fade}
                className={`${f.span || ""} rounded-2xl p-7 border transition-all duration-300 ${
                  f.dark ? "bg-ink text-cream border-ink-soft nt-grain relative overflow-hidden" : "bg-cardbg border-cardborder hover:border-sage/50 hover:shadow-md"
                }`}
              >
                <span className={`inline-grid h-11 w-11 place-items-center rounded-xl ${f.dark ? "bg-sage text-white" : "bg-sage-light text-sage-hover"}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className={`mt-5 font-serif text-xl ${f.dark ? "text-cream" : "text-ink"}`}>{f.title}</h3>
                <p className={`mt-2.5 leading-relaxed ${f.dark ? "text-cream/70" : "text-ink-muted"}`}>{f.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Reflection() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
      <div className="relative">
        <div className="overflow-hidden rounded-3xl border border-cardborder shadow-xl">
          <img
            src="https://images.unsplash.com/photo-1658279366796-e0c28623cd27?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
            alt="A person sitting quietly by a window, reflecting"
            className="w-full h-[440px] object-cover"
          />
        </div>
        <div className="absolute -bottom-6 -left-6 hidden sm:block rounded-2xl bg-cardbg border border-cardborder p-5 shadow-lg max-w-[220px]">
          <p className="font-mono text-xs text-sage-hover uppercase tracking-wider">This week</p>
          <p className="font-serif text-2xl text-ink mt-1">3 moments</p>
          <p className="text-sm text-ink-muted">handled differently</p>
        </div>
      </div>
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.15em] text-sage-hover">The evening debrief</p>
        <h2 className="mt-3 font-serif font-semibold tracking-tight text-3xl sm:text-4xl text-ink leading-tight">Reflection that actually changes the next conversation</h2>
        <p className="mt-5 text-lg text-ink-muted leading-relaxed">
          Most journaling stops at feelings. Next Time takes what you write and helps you rehearse a better version, so insight becomes a real, spoken sentence you can use.
        </p>
        <ul className="mt-7 space-y-4">
          {["Separates what happened from the story you told yourself", "Points out what was genuinely in your control", "Hands you a stronger line to say, then lets you practice it"].map((t) => (
            <li key={t} className="flex gap-3">
              <span className="mt-1 h-5 w-5 shrink-0 grid place-items-center rounded-full bg-sage text-white text-xs">✓</span>
              <span className="text-ink">{t}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const STORIES = [
  { name: "Devin R.", role: "Engineering manager", quote: "I used to freeze in tense conversations. After a week of role-plays I actually said the hard thing, calmly. It works because it makes you practice, not just think.", color: "#6B8E7B" },
  { name: "Amara K.", role: "New parent", quote: "The pattern spotter quietly showed me I shut down whenever I feel criticized. Naming it changed how I talk to my partner.", color: "#D99B58" },
  { name: "Leo M.", role: "Founder", quote: "Negotiating with an investor felt survivable because I'd already rehearsed the exact pushback with the AI. That's the whole pitch.", color: "#5A7E9A" },
];

function Testimonials() {
  return (
    <section id="stories" className="bg-ink text-cream nt-grain relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative">
        <div className="max-w-2xl">
          <p className="text-xs font-mono uppercase tracking-[0.15em] text-sage-muted">Stories</p>
          <h2 className="mt-3 font-serif font-semibold tracking-tight text-3xl sm:text-4xl text-cream">Small moments, handled better</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {STORIES.map((s, i) => (
            <motion.figure
              key={s.name}
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i} variants={fade}
              className="rounded-2xl bg-ink-soft/70 border border-white/10 p-7 backdrop-blur-sm"
            >
              <Quote className="h-7 w-7 text-sage-muted" />
              <blockquote className="mt-4 font-serif text-lg leading-relaxed text-cream/90">"{s.quote}"</blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <span className="h-10 w-10 rounded-full grid place-items-center font-semibold text-white" style={{ background: s.color }}>
                  {s.name[0]}
                </span>
                <span>
                  <span className="block text-sm font-semibold text-cream">{s.name}</span>
                  <span className="block text-xs text-cream/60">{s.role}</span>
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

const FAQS = [
  { q: "Is Next Time a therapist or mental-health service?", a: "No. Next Time is a practical practice tool for everyday difficult moments. It never diagnoses, and if something serious comes up it points you to real human support and crisis resources." },
  { q: "How does the AI role-play actually work?", a: "You pick a scenario and difficulty. The AI plays a realistic counterpart that pushes back but never makes success impossible. When you're done, it steps out of character and gives you specific, non-judgmental feedback on your choices." },
  { q: "What happens to what I write?", a: "Your moments and reflections are yours. Rejected interpretations are never reused as facts, you can export everything, delete AI memories, or remove all content at any time from settings." },
  { q: "Do I need the mobile app, or can I use the web?", a: "The iOS and Android apps give you the full experience on the go, and you can also demo moments and role-plays right here in your browser." },
  { q: "How much does it cost?", a: "You can start free with a few role-plays and moment analyses each month. Next Time Pro unlocks unlimited practice, a fresh daily scenario, custom scenarios, full pattern history, and data export." },
];

function Faq() {
  return (
    <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
      <div className="text-center">
        <p className="text-xs font-mono uppercase tracking-[0.15em] text-sage-hover">Questions</p>
        <h2 className="mt-3 font-serif font-semibold tracking-tight text-3xl sm:text-4xl text-ink">Good things to know</h2>
      </div>
      <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="faq-accordion">
        {FAQS.map((f, i) => (
          <AccordionItem key={i} value={`item-${i}`} className="rounded-2xl border border-cardborder bg-cardbg px-5 data-[state=open]:border-sage/50 data-[state=open]:shadow-md transition-all">
            <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-serif text-lg text-ink hover:no-underline py-5">
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-ink-muted leading-relaxed pb-5 text-base">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

function DownloadCta() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
      <div className="rounded-[2rem] bg-sage text-white p-10 lg:p-16 text-center relative overflow-hidden nt-grain">
        <div className="pointer-events-none absolute -top-16 -right-10 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
        <h2 className="font-serif font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl relative">Handle it better, Next Time.</h2>
        <p className="mt-4 text-lg text-white/85 max-w-xl mx-auto relative">Download Next Time and turn your next hard conversation into practice you've already done.</p>
        <div className="mt-8 flex justify-center relative">
          <AppStoreBadges dark />
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-cardborder bg-cream-alt">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid sm:grid-cols-2 gap-8">
        <div>
          <Logo />
          <p className="mt-4 text-sm text-ink-muted max-w-xs leading-relaxed">Learn from difficult moments, practice the conversations that matter, and prepare before they happen again.</p>
        </div>
        <div className="flex flex-col sm:items-end gap-6">
          <div className="grid grid-cols-2 gap-8 sm:text-right">
            <nav className="flex flex-col gap-2">
              {NAV.map((n) => (
                <a key={n.href} href={n.href} className="text-sm text-ink-muted hover:text-ink transition-colors">{n.label}</a>
              ))}
            </nav>
            <nav className="flex flex-col gap-2">
              <Link to="/privacy" className="text-sm text-ink-muted hover:text-ink transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-sm text-ink-muted hover:text-ink transition-colors">Terms of Service</Link>
              <Link to="/subscription-terms" className="text-sm text-ink-muted hover:text-ink transition-colors">Subscription Terms</Link>
              <Link to="/eula" className="text-sm text-ink-muted hover:text-ink transition-colors">EULA</Link>
              <Link to="/disclaimer" className="text-sm text-ink-muted hover:text-ink transition-colors">Disclaimer &amp; Safety</Link>
              <Link to="/support" className="text-sm text-ink-muted hover:text-ink transition-colors">Support</Link>
            </nav>
          </div>
          <p className="text-xs text-ink-muted sm:text-right">© {new Date().getFullYear()} Next Time. A practice tool, not a substitute for professional care.</p>
        </div>
      </div>
    </footer>
  );
}
